import type { MarketoFormLocalization } from './marketo-form-localization';

export type MarketoFieldQuery = {
	focus?: () => void;
	text?: () => string;
};

export type MarketoFormValidationApi = {
	getFormElem?: () => { find?: (selector: string) => MarketoFieldQuery };
	showErrorMessage?: (message: string, field: MarketoFieldQuery | HTMLInputElement) => void;
	submitable?: (canSubmit: boolean) => void;
	submittable?: (canSubmit: boolean) => void;
};

const EMAIL_PATTERN =
	/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

const BUSINESS_EMAIL_VALIDATION_KEY =
	'please enter your business email address. this form does not accept addresses from {{domain}}';
const EMAIL_VALIDATION_KEY = 'please enter a valid email address';

const FREE_EMAIL_DOMAINS = [
	'@gmail',
	'@yahoo',
	'@aol',
	'@comcast',
	'@hotmail',
	'@ymail',
	'@msn',
	'@mac',
	'@sina',
	'@freemail',
	'@me',
	'@icloud',
	'@live',
	'@gnail',
	'@gmsil',
	'@gmial',
	'@gamil',
	'@gail',
];

export const getRestrictedBusinessEmailDomain = (email: string): string | undefined => {
	const value = email.trim().toLowerCase();
	const atIndex = value.indexOf('@');

	if (atIndex === -1) {
		return undefined;
	}

	const domain = value.substring(atIndex);
	const domainParts = domain.split('.');
	const isFreeEmailDomain = FREE_EMAIL_DOMAINS.some((freeEmailDomain) => {
		if (freeEmailDomain === '@comcast' && domain === '@comcast.com') {
			return false;
		}

		return domainParts.includes(freeEmailDomain);
	});

	return isFreeEmailDomain ? domain.substring(1) : undefined;
};

export const setMarketoSubmittable = (form: MarketoFormValidationApi, canSubmit: boolean): void => {
	if (form.submittable) {
		form.submittable(canSubmit);
		return;
	}

	form.submitable?.(canSubmit);
};

const getLocalizedValidationMessage = (
	localization: MarketoFormLocalization,
	key: string,
	fallback: string,
): string => localization.validationMessages[key] ?? fallback;

const getBusinessEmailValidationMessage = (
	localization: MarketoFormLocalization,
	domain: string,
): string =>
	getLocalizedValidationMessage(
		localization,
		BUSINESS_EMAIL_VALIDATION_KEY,
		'Please enter your business email address. This form does not accept addresses from {{domain}}',
	).replace('{{domain}}', domain);

const findEmailInput = (formElement: HTMLElement): HTMLInputElement | null =>
	formElement.querySelector<HTMLInputElement>('input#Email, input[name="Email"]');

const getMarketoEmailField = (
	form: MarketoFormValidationApi,
	emailInput: HTMLInputElement,
): MarketoFieldQuery | HTMLInputElement => form.getFormElem?.().find?.('#Email') ?? emailInput;

const showEmailValidationError = (
	form: MarketoFormValidationApi,
	formElement: HTMLElement,
	message: string,
): void => {
	const emailInput = findEmailInput(formElement);

	if (!emailInput) {
		return;
	}

	const marketoEmailField = getMarketoEmailField(form, emailInput);
	marketoEmailField.focus?.();
	emailInput.classList.add('mktoInvalid');
	emailInput.setAttribute('aria-invalid', 'true');
	form.showErrorMessage?.(message, marketoEmailField);
};

export const validateBusinessEmailDomain = (
	form: MarketoFormValidationApi,
	formElement: HTMLElement,
	localization: MarketoFormLocalization,
): boolean => {
	const emailInput = findEmailInput(formElement);
	const emailValue = emailInput?.value.trim() ?? '';

	if (!emailInput || !emailValue) {
		return true;
	}

	if (!EMAIL_PATTERN.test(emailValue)) {
		const message = getLocalizedValidationMessage(
			localization,
			EMAIL_VALIDATION_KEY,
			'Please enter a valid email address',
		);
		setMarketoSubmittable(form, false);
		showEmailValidationError(form, formElement, message);
		return false;
	}

	const restrictedDomain = getRestrictedBusinessEmailDomain(emailValue);
	if (restrictedDomain) {
		setMarketoSubmittable(form, false);
		showEmailValidationError(
			form,
			formElement,
			getBusinessEmailValidationMessage(localization, restrictedDomain),
		);
		return false;
	}

	setMarketoSubmittable(form, true);
	return true;
};
